import React, { useEffect, useMemo, useState } from "react";
import { ITicket } from "../models/ITicket";
import { getAllTickets, linkTicket } from "../services/ticketService";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  ticketId: string;
  onLinked: () => void;
}

const CLOSED_STATUS_ID = "c11e154e-6839-40be-9c9d-8209693034f4";

function truncate(text: string, max: number): string {
  if (!text) return "";
  return text.length > max ? text.slice(0, max) + "…" : text;
}

export default function LinkTicketModal({ isOpen, onClose, ticketId, onLinked }: Props) {
  const [searchTerm, setSearchTerm]     = useState("");
  const [tickets, setTickets]           = useState<ITicket[]>([]);
  const [loading, setLoading]           = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<ITicket | null>(null);
  const [linkType, setLinkType]         = useState<1 | 2 | null>(null);
  const [linking, setLinking]           = useState(false);
  const [error, setError]               = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setSearchTerm("");
    setSelectedTicket(null);
    setLinkType(null);
    setError("");
    setLoading(true);
    // GetAllTickets statusId verilmeden çağrıldığında kapalı ticketları döndürmüyor,
    // bu yüzden kapalı ticketları ayrıca statusId ile çekip birleştiriyoruz.
    Promise.all([
      getAllTickets({ pageIndex: 0, pageSize: 500 }),
      getAllTickets({ pageIndex: 0, pageSize: 500, statusId: CLOSED_STATUS_ID }),
    ])
      .then(([openRes, closedRes]) => {
        const items: ITicket[] = [
          ...(openRes.data?.items ?? []),
          ...(closedRes.data?.items ?? []),
        ].filter((t: ITicket) => t.id !== ticketId);
        setTickets(items);
      })
      .catch(() => setTickets([]))
      .finally(() => setLoading(false));
  }, [isOpen, ticketId]);

  // Başlıkta geçen kelimelere veya ticket no'ya göre client-side arama
  const filteredTickets = useMemo(() => {
    const term = searchTerm.trim().toLocaleLowerCase("tr");
    if (!term) return tickets;
    return tickets.filter((t) => {
      const title = (t.title ?? "").toLocaleLowerCase("tr");
      const no    = (t.ticketNo ?? "").toLocaleLowerCase("tr");
      return title.includes(term) || no.includes(term);
    });
  }, [tickets, searchTerm]);

  const handleLink = async () => {
    if (!selectedTicket || !linkType) return;
    setLinking(true);
    setError("");
    try {
      await linkTicket({ ticketId, parentTicketId: selectedTicket.id, linkType });
      onClose();
      onLinked();
    } catch {
      setError("Ticket bağlanamadı. Lütfen tekrar deneyin.");
    } finally {
      setLinking(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 20, width: "100%", maxWidth: 480, maxHeight: "85vh",
          boxShadow: "0 12px 48px rgba(0,0,0,0.22)", overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "20px 24px 18px",
          background: "linear-gradient(135deg, #4f46e5, #4338ca)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: "rgba(255,255,255,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <i className="bi bi-link-45deg text-white" style={{ fontSize: "1.2rem" }}></i>
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: "1rem", color: "#fff" }}>Ticketi Bağla</div>
              <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.75)" }}>Bağlanacak ticketi arayın ve seçin</div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.15)", border: "none",
              borderRadius: 8, width: 32, height: 32,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", color: "#fff", fontSize: "1.1rem",
            }}
          >×</button>
        </div>

        {/* Search */}
        <div style={{ padding: "14px 20px", borderBottom: "1.5px solid #f1f5f9", flexShrink: 0 }}>
          <div style={{ position: "relative" }}>
            <i className="bi bi-search" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "0.85rem" }}></i>
            <input
              type="text"
              autoFocus
              placeholder="Başlık veya içerik ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%", padding: "9px 12px 9px 34px", boxSizing: "border-box",
                border: "1.5px solid #e5e7eb", borderRadius: 10,
                fontSize: "0.85rem", outline: "none", color: "#111827",
              }}
            />
          </div>
        </div>

        {/* List */}
        <div style={{ padding: "10px 14px", overflowY: "auto", flex: 1, minHeight: 200 }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <div className="spinner-border text-primary spinner-border-sm" role="status" />
            </div>
          ) : filteredTickets.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af" }}>
              <i className="bi bi-inbox" style={{ fontSize: "1.8rem", display: "block", marginBottom: 8, opacity: 0.4 }}></i>
              <span style={{ fontSize: "0.82rem" }}>Ticket bulunamadı</span>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {filteredTickets.map((t) => {
                const preview = t.note?.trim() || t.messages?.[0]?.message?.trim() || "";
                const selected = selectedTicket?.id === t.id;
                return (
                  <div
                    key={t.id}
                    onClick={() => setSelectedTicket(t)}
                    style={{
                      padding: "10px 12px", borderRadius: 10, cursor: "pointer",
                      background: selected ? "#eef2ff" : "#f9fafb",
                      border: `1.5px solid ${selected ? "#6366f1" : "#f1f5f9"}`,
                      transition: "background 0.15s, border-color 0.15s",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {t.ticketNo && (
                        <span style={{ fontSize: "0.68rem", fontWeight: 700, color: "#6366f1", flexShrink: 0 }}>
                          <i className="bi bi-hash"></i>{t.ticketNo}
                        </span>
                      )}
                      <span style={{
                        fontWeight: 700, fontSize: "0.85rem", color: "#111827",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0,
                      }}>
                        {t.title}
                      </span>
                      {selected && <i className="bi bi-check-circle-fill" style={{ color: "#6366f1", flexShrink: 0 }}></i>}
                    </div>
                    {preview && (
                      <div style={{ fontSize: "0.76rem", color: "#9ca3af", marginTop: 4, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                        {truncate(preview, 90)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer: link type + submit */}
        <div style={{ padding: "14px 20px", borderTop: "1.5px solid #f1f5f9", background: "#fafafa", flexShrink: 0 }}>
          {error && (
            <div style={{
              marginBottom: 10, padding: "8px 12px", borderRadius: 9, fontSize: "0.8rem",
              background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626",
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <i className="bi bi-exclamation-circle-fill"></i>{error}
            </div>
          )}
          {selectedTicket && (
            <div style={{
              marginBottom: 12, padding: "8px 12px", borderRadius: 9,
              background: "#eef2ff", border: "1px solid #c7d2fe",
              fontSize: "0.78rem", color: "#4338ca",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              <i className="bi bi-link-45deg me-1"></i>Seçilen: <strong>{selectedTicket.title}</strong>
            </div>
          )}
          <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
            <label style={{
              flex: 1, display: "flex", alignItems: "center", gap: 8, padding: "9px 12px",
              borderRadius: 10, cursor: "pointer",
              background: linkType === 1 ? "#eef2ff" : "#fff",
              border: `1.5px solid ${linkType === 1 ? "#6366f1" : "#e5e7eb"}`,
            }}>
              <input type="radio" name="linkType" checked={linkType === 1} onChange={() => setLinkType(1)} style={{ accentColor: "#6366f1", cursor: "pointer" }} />
              <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "#374151" }}>Kopyası</span>
            </label>
            <label style={{
              flex: 1, display: "flex", alignItems: "center", gap: 8, padding: "9px 12px",
              borderRadius: 10, cursor: "pointer",
              background: linkType === 2 ? "#eef2ff" : "#fff",
              border: `1.5px solid ${linkType === 2 ? "#6366f1" : "#e5e7eb"}`,
            }}>
              <input type="radio" name="linkType" checked={linkType === 2} onChange={() => setLinkType(2)} style={{ accentColor: "#6366f1", cursor: "pointer" }} />
              <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "#374151" }}>Alt Kaydı</span>
            </label>
          </div>
          <button
            onClick={handleLink}
            disabled={!selectedTicket || !linkType || linking}
            style={{
              width: "100%", padding: 10, borderRadius: 10, border: "none",
              background: selectedTicket && linkType && !linking ? "linear-gradient(135deg, #4f46e5, #4338ca)" : "#e5e7eb",
              color: selectedTicket && linkType && !linking ? "#fff" : "#9ca3af",
              fontWeight: 700, fontSize: "0.88rem",
              cursor: selectedTicket && linkType && !linking ? "pointer" : "not-allowed",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              transition: "background 0.15s",
            }}
          >
            {linking
              ? <><span className="spinner-border spinner-border-sm" role="status" /> Bağlanıyor...</>
              : <><i className="bi bi-link-45deg"></i> Ticketi Bağla</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}
